import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faInfo, faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { CORAL, MUTED, NAVY, qbrVisualizationRules } from "../constants";

export function RulesInfoButton({ title, rulesKey }) {
  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const rulesRef = useRef(null);
  const rules = rulesKey ? qbrVisualizationRules[rulesKey] : null;

  useEffect(() => {
    if (!isRulesOpen) return undefined;
    const handlePointerDown = (event) => {
      if (rulesRef.current && !rulesRef.current.contains(event.target)) {
        setIsRulesOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsRulesOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isRulesOpen]);

  if (!rules) return null;

  return (
    <div ref={rulesRef} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setIsRulesOpen((current) => !current)}
        className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-black transition-colors"
        style={{
          border: `1px solid ${NAVY}28`,
          color: isRulesOpen ? "white" : NAVY,
          background: isRulesOpen ? NAVY : "white",
        }}
        aria-expanded={isRulesOpen}
        aria-label={`Show filters applied for ${title}`}
        title="Filters applied"
      >
        <FontAwesomeIcon icon={faInfo} />
      </button>
      {isRulesOpen && (
        <div
          className="absolute left-0 top-7 z-30 w-[min(82vw,320px)] rounded-lg border bg-white p-3 text-left shadow-xl"
          style={{ borderColor: "#d6dfeb", color: NAVY }}
        >
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: MUTED }}>
                Filters Applied
              </p>
              <p className="mt-1 text-sm font-black leading-snug">{title}</p>
            </div>
            <button
              type="button"
              onClick={() => setIsRulesOpen(false)}
              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-[11px] transition-colors hover:bg-[#f1f5fb]"
              style={{ color: MUTED }}
              aria-label="Close filters"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div className="max-h-[240px] space-y-3 overflow-y-auto pr-1">
            {rules.groups.map((group) => (
              <div key={group.title}>
                {rules.groups.length > 1 && (
                  <p className="mb-1.5 text-[11px] font-black uppercase tracking-[0.08em]" style={{ color: CORAL }}>
                    {group.title}
                  </p>
                )}
                <ul className="space-y-1.5">
                  {group.items.map((item) => (
                    <li key={item} className="flex gap-2 text-xs font-semibold leading-relaxed" style={{ color: "#3a577f" }}>
                      <span className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full" style={{ background: CORAL }} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function SectionTitle({ title, icon, rulesKey, onExport, isExporting, disabled }) {
  return (
    <div
      className="mb-3 flex flex-col gap-3 pb-2.5 sm:flex-row sm:items-center sm:justify-between"
      style={{ borderBottom: `1px solid #d4dbe8` }}
    >
      <div className="flex min-w-0 items-center gap-2.5">
        <div
          className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${CORAL}18` }}
        >
          <FontAwesomeIcon icon={icon} className="text-[10px]" style={{ color: CORAL }} />
        </div>
        <p className="min-w-0 text-xs font-black uppercase leading-relaxed tracking-[0.18em]" style={{ color: NAVY }}>
          {title}
        </p>
        <RulesInfoButton title={title} rulesKey={rulesKey} />
      </div>
      {onExport && (
        <button
          data-export-btn
          onClick={onExport}
          disabled={isExporting || disabled}
          className="flex w-fit shrink-0 items-center gap-1.5 self-start whitespace-nowrap rounded-lg px-3 py-1.5 text-[11px] font-bold transition-colors disabled:opacity-50 sm:self-auto"
          style={{ border: `1px solid ${NAVY}28`, color: NAVY }}
          title="Export as PNG"
        >
          <FontAwesomeIcon
            icon={isExporting ? faSpinner : faDownload}
            className={`text-[10px] ${isExporting ? "animate-spin" : ""}`}
          />
          {isExporting ? "Exporting…" : "Export PNG"}
        </button>
      )}
    </div>
  );
}
